import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '/api';

interface SystemStats {
  totalUsers: number;
  totalPens: number;
  totalDoses: number;
  activeUsers: number;
  totalCostSavings: number;
  averageCostPerDose: number;
  topCountries: Array<{ country: string; count: number }>;
  recentActivity: Array<{
    type: string;
    description: string;
    timestamp: string;
    userId?: string;
  }>;
  pricingStats?: {
    strengthBasedUsage: number;
    defaultPricingUsage: number;
    countriesWithGaps: string[];
    accuracyPercentage: number;
    lastReset: string;
  };
}

interface FeedbackItem {
  _id: string;
  message?: string;
  rating?: number;
  email?: string;
  userId?: { _id: string; email: string } | null;
  context?: { page?: string; appVersion?: string; trigger?: string };
  createdAt: string;
}

const RATING_EMOJI: Record<number, string> = { 1: '😞', 2: '😕', 3: '😐', 4: '🙂', 5: '😍' };

// DOS-38: PostHog identifies users by their Mongo _id (see AuthContext identifyUser),
// so a feedback item's userId deep-links straight to that person's analytics profile.
const POSTHOG_PROJECT_ID = process.env.REACT_APP_POSTHOG_PROJECT_ID;
const posthogPersonUrl = (userId: string): string | null =>
  POSTHOG_PROJECT_ID
    ? `https://eu.posthog.com/project/${POSTHOG_PROJECT_ID}/person/${encodeURIComponent(userId)}`
    : null;

const AdminDashboardPage: React.FC = () => {
  const { isAuthenticated, userType } = useAuth();
  const [stats, setStats] = useState<SystemStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // DOS-11: pen expiration window config
  const [penExpirationDays, setPenExpirationDays] = useState<number | null>(null);
  const [expiryAllowed, setExpiryAllowed] = useState<number[]>([60, 90, 120]);
  const [savingExpiry, setSavingExpiry] = useState(false);
  const [expiryMsg, setExpiryMsg] = useState<string | null>(null);

  // DOS-38: recent user feedback
  const [feedback, setFeedback] = useState<FeedbackItem[]>([]);
  const [feedbackError, setFeedbackError] = useState<string | null>(null);

  // Only allow admin users
  const isAdmin = userType === 'admin';

  useEffect(() => {
    if (!isAuthenticated || !isAdmin) return;
    fetchSystemStats();
    fetchPenExpiration();
    fetchFeedback();
  }, [isAuthenticated, isAdmin]);

  const fetchFeedback = async () => {
    const token = localStorage.getItem('token');
    try {
      const res = await fetch(`${API_BASE_URL}/admin/feedback?limit=50`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!res.ok) throw new Error('Failed to fetch feedback');
      setFeedback(await res.json());
      setFeedbackError(null);
    } catch (err: any) {
      setFeedbackError(err.message);
    }
  };

  const fetchPenExpiration = async () => {
    const token = localStorage.getItem('token');
    try {
      const res = await fetch(`${API_BASE_URL}/config/pen-expiration`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!res.ok) return;
      const data = await res.json();
      setPenExpirationDays(data.expirationDays);
      if (Array.isArray(data.allowedValues)) setExpiryAllowed(data.allowedValues);
    } catch { /* non-fatal */ }
  };

  const savePenExpiration = async (days: number) => {
    const token = localStorage.getItem('token');
    setSavingExpiry(true);
    setExpiryMsg(null);
    try {
      const res = await fetch(`${API_BASE_URL}/admin/pen-expiration`, {
        method: 'PATCH',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ expirationDays: days }),
      });
      if (!res.ok) throw new Error('save failed');
      const data = await res.json();
      setPenExpirationDays(data.expirationDays);
      setExpiryMsg('Saved');
    } catch {
      setExpiryMsg('Failed to save');
    } finally {
      setSavingExpiry(false);
    }
  };

  const fetchSystemStats = async () => {
    const token = localStorage.getItem('token');
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/admin/stats`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch system stats');
      }
      
      const data = await response.json();
      setStats(data);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency: 'AUD'
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-GB', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (!isAuthenticated) {
    return <div className="p-8 text-center">Please log in as an admin to access this page.</div>;
  }
  
  if (!isAdmin) {
    return <div className="p-8 text-center text-red-600">Access denied. Admins only.</div>;
  }

  return (
    <div className="max-w-7xl mx-auto p-8">
      <h1 className="text-4xl font-bold mb-8 font-['Space_Grotesk'] bg-gradient-to-r from-emerald-600 to-teal-600 bg-clip-text text-transparent">Admin Dashboard</h1>

      {/* DOS-11: Pen expiration window configuration */}
      <div className="bg-white p-6 rounded-lg shadow mb-8">
        <h2 className="text-xl font-semibold mb-2 text-purple-600">Pen Expiration Window</h2>
        <p className="text-sm text-gray-600 mb-4">
          Days after a pen&apos;s <strong>first injection</strong> before it is automatically marked expired.
          Changes apply to <strong>newly created pens only</strong> — existing pens keep the window they were created with.
        </p>
        <div className="flex items-center gap-3">
          <label htmlFor="pen-expiry-select" className="text-sm font-medium text-gray-700">Default window</label>
          <select
            id="pen-expiry-select"
            value={penExpirationDays ?? ''}
            onChange={(e) => savePenExpiration(parseInt(e.target.value, 10))}
            disabled={savingExpiry || penExpirationDays === null}
            className="border rounded px-3 py-2"
          >
            {penExpirationDays === null && <option value="">Loading…</option>}
            {expiryAllowed.map(d => <option key={d} value={d}>{d} days</option>)}
          </select>
          {savingExpiry && <span className="text-sm text-gray-500">Saving…</span>}
          {expiryMsg && !savingExpiry && (
            <span className={`text-sm ${expiryMsg === 'Saved' ? 'text-green-600' : 'text-red-600'}`}>{expiryMsg}</span>
          )}
        </div>
      </div>

      {/* DOS-38: recent user feedback */}
      <div className="bg-white p-6 rounded-lg shadow mb-8">
        <h2 className="text-xl font-semibold mb-2 text-purple-600">User Feedback</h2>
        <p className="text-sm text-gray-600 mb-4">
          Submitted via the in-app feedback widget and post-dose prompts. Most recent first.
        </p>
        {feedbackError ? (
          <div className="text-red-600 p-3 bg-red-50 rounded">{feedbackError}</div>
        ) : feedback.length === 0 ? (
          <div className="text-gray-500 text-sm">No feedback yet.</div>
        ) : (
          <div className="max-h-96 overflow-y-auto divide-y divide-gray-100">
            {feedback.map((item) => (
              <div key={item._id} className="py-3 flex gap-3 items-start">
                <div className="text-2xl w-8 text-center flex-shrink-0" title={item.rating ? `Rated ${item.rating}/5` : 'No rating'}>
                  {item.rating ? RATING_EMOJI[item.rating] : '💬'}
                </div>
                <div className="min-w-0 flex-1">
                  {item.message && <p className="m-0 text-gray-800 whitespace-pre-wrap break-words">{item.message}</p>}
                  <div className="text-xs text-gray-500 mt-1 flex flex-wrap gap-x-3">
                    <span>{item.userId?.email || item.email || 'Anonymous'}</span>
                    {item.context?.page && <span>{item.context.page}</span>}
                    {item.context?.trigger === 'post_dose' && <span className="text-teal-600">post-dose prompt</span>}
                    {item.context?.appVersion && <span>v{item.context.appVersion}</span>}
                    <span>{formatDate(item.createdAt)}</span>
                    {item.userId?._id && posthogPersonUrl(item.userId._id) && (
                      <a
                        href={posthogPersonUrl(item.userId._id)!}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-purple-600 hover:underline font-medium"
                        title="Open this user's analytics profile in PostHog"
                      >
                        PostHog ↗
                      </a>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {loading ? (
        <div className="text-center py-8">Loading system statistics...</div>
      ) : error ? (
        <div className="text-red-600 mb-4 p-4 bg-red-50 rounded">{error}</div>
      ) : stats ? (
        <div className="space-y-8">
          {/* Quick Actions */}
          <div className="bg-white p-6 rounded-lg shadow">
            <h2 className="text-xl font-semibold mb-4 text-purple-600">Quick Actions</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <Link
                to="/admin/pricing"
                className="bg-gradient-to-br from-emerald-500 to-teal-500 text-white p-6 rounded-2xl hover:from-emerald-600 hover:to-teal-600 transition-all duration-300 text-center shadow-lg hover:shadow-xl hover:-translate-y-1"
              >
                <div className="text-2xl mb-2">💰</div>
                <div className="font-semibold">Manage Pricing</div>
                <div className="text-sm opacity-90">Update pen costs by country and strength</div>
              </Link>
              <Link
                to="/admin/users"
                className="bg-blue-600 text-white p-4 rounded-lg hover:bg-blue-700 transition-colors text-center"
              >
                <div className="text-2xl mb-2">👥</div>
                <div className="font-semibold">User Management</div>
                <div className="text-sm opacity-90">View and manage user accounts</div>
              </Link>
              <Link
                to="/admin/email-campaigns"
                className="bg-purple-600 text-white p-4 rounded-lg hover:bg-purple-700 transition-colors text-center"
              >
                <div className="text-2xl mb-2">📧</div>
                <div className="font-semibold">Email Campaigns</div>
                <div className="text-sm opacity-90">Manage user communications</div>
              </Link>
              <Link
                to="/admin/reddit-marketing"
                className="bg-red-600 text-white p-4 rounded-lg hover:bg-red-700 transition-colors text-center"
              >
                <div className="text-2xl mb-2">🤖</div>
                <div className="font-semibold">Reddit Marketing</div>
                <div className="text-sm opacity-90">Find and engage with relevant posts</div>
              </Link>
              <div className="bg-green-600 text-white p-4 rounded-lg text-center">
                <div className="text-2xl mb-2">📊</div>
                <div className="font-semibold">System Overview</div>
                <div className="text-sm opacity-90">Current page - system statistics</div>
              </div>
            </div>
          </div>

          {/* Key Metrics */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-white p-6 rounded-lg shadow">
              <div className="flex items-center">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
                  </svg>
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Total Users</p>
                  <p className="text-2xl font-semibold text-gray-900">{stats.totalUsers}</p>
                </div>
              </div>
            </div>

            <div className="bg-white p-6 rounded-lg shadow">
              <div className="flex items-center">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Total Pens</p>
                  <p className="text-2xl font-semibold text-gray-900">{stats.totalPens}</p>
                </div>
              </div>
            </div>

            <div className="bg-white p-6 rounded-lg shadow">
              <div className="flex items-center">
                <div className="p-2 bg-green-100 rounded-lg">
                  <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                  </svg>
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Total Doses</p>
                  <p className="text-2xl font-semibold text-gray-900">{stats.totalDoses}</p>
                </div>
              </div>
            </div>

            <div className="bg-white p-6 rounded-lg shadow">
              <div className="flex items-center">
                <div className="p-2 bg-yellow-100 rounded-lg">
                  <svg className="w-6 h-6 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                  </svg>
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Total Savings</p>
                  <p className="text-2xl font-semibold text-gray-900">{formatCurrency(stats.totalCostSavings)}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Pricing Accuracy Alert */}
          {stats.pricingStats && (
            <div className="bg-white p-6 rounded-lg shadow">
              <div className={`rounded-lg p-4 ${
                stats.pricingStats.accuracyPercentage < 90 
                  ? 'bg-red-50 border border-red-200' 
                  : stats.pricingStats.accuracyPercentage < 95 
                  ? 'bg-yellow-50 border border-yellow-200' 
                  : 'bg-green-50 border border-green-200'
              }`}>
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">
                      Pricing Accuracy Alert
                    </h3>
                    <p className="text-sm text-gray-600 mb-3">
                      {stats.pricingStats.accuracyPercentage < 90 
                        ? '⚠️ High usage of default pricing detected. Consider adding strength-specific pricing for better accuracy.'
                        : stats.pricingStats.accuracyPercentage < 95 
                        ? '⚠️ Some default pricing usage detected. Review countries with pricing gaps.'
                        : '✅ Excellent pricing accuracy! All calculations using strength-specific pricing.'
                      }
                    </p>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                      <div>
                        <span className="font-medium text-gray-700">Accuracy:</span>
                        <span className={`ml-2 font-bold ${
                          stats.pricingStats.accuracyPercentage < 90 ? 'text-red-600' :
                          stats.pricingStats.accuracyPercentage < 95 ? 'text-yellow-600' : 'text-green-600'
                        }`}>
                          {stats.pricingStats.accuracyPercentage}%
                        </span>
                      </div>
                      <div>
                        <span className="font-medium text-gray-700">Strength-based:</span>
                        <span className="ml-2 text-green-600 font-bold">{stats.pricingStats.strengthBasedUsage}</span>
                      </div>
                      <div>
                        <span className="font-medium text-gray-700">Default fallback:</span>
                        <span className="ml-2 text-red-600 font-bold">{stats.pricingStats.defaultPricingUsage}</span>
                      </div>
                    </div>
                    {stats.pricingStats.countriesWithGaps.length > 0 && (
                      <div className="mt-3">
                        <span className="font-medium text-gray-700">Countries needing pricing data:</span>
                        <div className="mt-1 flex flex-wrap gap-2">
                          {stats.pricingStats.countriesWithGaps.map(country => (
                            <span key={country} className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                              {country}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                  <div className="text-right">
                    <Link 
                      to="/admin/pricing" 
                      className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    >
                      Manage Pricing
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Additional Stats */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="bg-white p-6 rounded-lg shadow">
              <h3 className="text-lg font-semibold mb-4 text-gray-800">User Activity</h3>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-gray-600">Active Users (30 days)</span>
                  <span className="font-semibold">{stats.activeUsers}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Average Cost per Dose</span>
                  <span className="font-semibold">{formatCurrency(stats.averageCostPerDose)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Average Doses per User</span>
                  <span className="font-semibold">
                    {stats.totalUsers > 0 ? (stats.totalDoses / stats.totalUsers).toFixed(1) : '0'}
                  </span>
                </div>
              </div>
            </div>

            <div className="bg-white p-6 rounded-lg shadow">
              <h3 className="text-lg font-semibold mb-4 text-gray-800">Top Countries</h3>
              <div className="space-y-2">
                {stats.topCountries.map((country, index) => (
                  <div key={country.country} className="flex justify-between items-center">
                    <span className="text-gray-600">{country.country}</span>
                    <div className="flex items-center">
                      <div className="w-20 bg-gray-200 rounded-full h-2 mr-2">
                        <div 
                          className="bg-purple-600 h-2 rounded-full" 
                          style={{ 
                            width: `${(country.count / Math.max(...stats.topCountries.map(c => c.count))) * 100}%` 
                          }}
                        ></div>
                      </div>
                      <span className="font-semibold text-sm">{country.count}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Recent Activity */}
          <div className="bg-white p-6 rounded-lg shadow">
            <h3 className="text-lg font-semibold mb-4 text-gray-800">Recent Activity</h3>
            <div className="space-y-3">
              {stats.recentActivity.map((activity, index) => (
                <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded">
                  <div>
                    <p className="font-medium text-gray-800">{activity.description}</p>
                    <p className="text-sm text-gray-600">{activity.type}</p>
                  </div>
                  <span className="text-sm text-gray-500">{formatDate(activity.timestamp)}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default AdminDashboardPage; 